import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { axios } from '@/lib/axios/axios';
import { InfiniteQueryConfig } from '@/lib/react-query/react-query';
import {
  MESSAGING_API_BASE,
  messagingQueryKeys,
  DEFAULT_CHANNELS_LIMIT,
} from '../config';
import { ChannelsListResponse, ChannelTypeEnum } from '../types/backend';

export type ChannelsListQuery = {
  type: ChannelTypeEnum;
  section?: 'active' | 'archived';
  limit?: number;
  offset?: number;
};

const getChannels = async (
  params: ChannelsListQuery
): Promise<ChannelsListResponse> => {
  const {
    type,
    section = 'active',
    limit = DEFAULT_CHANNELS_LIMIT,
    offset = 0,
  } = params;

  const queryParams = new URLSearchParams({
    type,
    section,
    limit: String(limit),
    offset: String(offset),
  });

  return axios.get(`${MESSAGING_API_BASE}/channels?${queryParams}`);
};

type QueryFnType = typeof getChannels;

type UseChannelsOptions = {
  type: ChannelTypeEnum;
  section?: 'active' | 'archived';
  // initialLimit: number used for the first page load
  initialLimit?: number;
  // nextLimit: number used for subsequent 'Load more' pages
  nextLimit?: number;
  config?: InfiniteQueryConfig<QueryFnType>;
  // When true, auto-fetch will sequentially load all pages until backend 'count' is reached.
  // Default: false to avoid large numbers of background requests. Make opt-in.
  autoFetch?: boolean;
};

export const useChannels = ({
  type,
  section = 'active',
  initialLimit = DEFAULT_CHANNELS_LIMIT,
  nextLimit = DEFAULT_CHANNELS_LIMIT,
  config,
  autoFetch = false,
}: UseChannelsOptions) => {
  const query = useInfiniteQuery({
    queryKey: [
      ...messagingQueryKeys.channelsList({
        type,
        section,
        limit: initialLimit,
      }),
      'nextLimit',
      nextLimit,
    ],
    queryFn: ({ pageParam = 0 }) => {
      const limitToUse = pageParam === 0 ? initialLimit : nextLimit;
      return getChannels({
        type,
        section,
        limit: limitToUse,
        offset: pageParam,
      });
    },
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      const currentItemCount = allPages.reduce(
        (sum, page) => sum + page.rows.length,
        0
      );

      if (currentItemCount < lastPage.count) {
        return currentItemCount; // Return next offset
      }

      return undefined; // No more pages
    },
    ...config,
  });

  // Auto-fetch subsequent pages until backend 'count' is reached.
  // This behavior is opt-in via the `autoFetch` option to avoid generating large
  // numbers of background requests. Callers who want full prefetching can set
  // `autoFetch: true` explicitly.
  useEffect(() => {
    if (!autoFetch) return;

    let mounted = true;

    const doAutoFetch = async () => {
      if (!query.data) return;
      try {
        while (mounted && query.hasNextPage && !query.isFetching) {
          // eslint-disable-next-line no-await-in-loop
          await query.fetchNextPage();
        }
      } catch (err) {
        // swallow errors; leave query state to react-query
      }
    };

    doAutoFetch();

    return () => {
      mounted = false;
    };
  }, [
    autoFetch,
    query.data,
    query.hasNextPage,
    query.isFetching,
    query.fetchNextPage,
  ]);

  return query;
};
